import { Children, isValidElement, useEffect, useMemo, useRef } from 'react';
import type { ComponentPropsWithoutRef, ElementType, PropsWithChildren } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { toLinearGradient } from '#src/app/shared/utils/color';

import { useScrollContainer } from './hooks/useScrollContainer';
import { useScrollEnd } from './hooks/useScrollEnd';
import { NavigationButtonsV2 } from './NavigationButtonsV2';

type Props = PropsWithChildren<
	Omit<ComponentPropsWithoutRef<'section'>, 'color'> & {
		label?: string;
		as?: ElementType;
		backgroundColor?: string;
		ref?: React.Ref<HTMLElement>;
		listClassName?: string;
		itemClassName?: string;
		onActiveIndexChange?: (activeIndex: number, totalItems: number) => void;
	}
>;

const OUTER_GRID_STYLE: React.CSSProperties = {
	gridTemplateColumns:
		'[carousel-gutter] calc(36px + (8px * 2)) [carousel-scroller] 1fr [carousel-gutter] calc(36px + (8px * 2))',
	gridTemplateRows: '[carousel-scroller] 1fr',
};

// focus-visible:ring-inset (rather than the browser's default outset outline) so the indicator
// renders inside the box and isn't clipped by the parent <As>'s overflow-hidden (CHANGE-97827).
const LIST_CLASS =
	'col-[1/-1] row-[1] grid grid-flow-col gap-4 overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-2.5 motion-safe:scroll-smooth md:pb-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stroke-info-strong [scrollbar-color:transparent_transparent] [scrollbar-width:thin] hover:[scrollbar-color:var(--color-stroke-weak)_transparent] md:[scrollbar-width:none] [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-0 md:[&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:bg-stroke-weak';

export const ScrollContainerV2: React.FC<Props> = ({
	children,
	label,
	backgroundColor = 'transparent',
	as: As = 'section',
	className,
	listClassName,
	itemClassName,
	onActiveIndexChange,
	style,
	ref,
	...rest
}) => {
	const carouselRef = useRef<HTMLElement | null>(null);
	const { data, actions } = useScrollContainer(carouselRef);

	useScrollEnd(carouselRef, actions.handleScrollEnd);

	const totalItems = useMemo(() => Children.count(children), [children]);

	// Latest-ref pattern so this only re-fires when activeIndex/totalItems actually change, not
	// whenever the caller passes a new (e.g. inline) callback reference.
	const onActiveIndexChangeRef = useRef(onActiveIndexChange);
	useEffect(() => {
		onActiveIndexChangeRef.current = onActiveIndexChange;
	});
	useEffect(() => {
		onActiveIndexChangeRef.current?.(data.activeIndex, totalItems);
	}, [data.activeIndex, totalItems]);

	if (totalItems === 0) return null;

	return (
		<As
			aria-label={label}
			ref={ref}
			className={cn('relative grid overflow-hidden', className)}
			style={{ ...OUTER_GRID_STYLE, ...style }}
			{...rest}
		>
			<NavigationButtonsV2
				previous={{ onClick: actions.scrollToLeft, disabled: data.scrollLeftDisabled }}
				next={{ onClick: actions.scrollToRight, disabled: data.scrollRightDisabled }}
				prevFade={toLinearGradient(backgroundColor, 'left')}
				nextFade={toLinearGradient(backgroundColor, 'right')}
			/>
			<ul
				id="scroll-container-list"
				role="list"
				ref={carouselRef as React.Ref<HTMLUListElement>}
				className={cn(LIST_CLASS, listClassName)}
				// Unconditional, not gated on a "is this actually scrollable" measurement: Chromium
				// makes a scrollable overflow region implicitly focusable, Safari doesn't, and
				// there's no reliable way to keep that measurement live against every layout change
				// (viewport resize, device rotation, a hidden/shown responsive twin of this same
				// tree -- see Main/index.tsx's md:hidden/md:block pair). CHANGE-94489 took the same
				// unconditional approach for the Media Mentions scroll region on this same page, and
				// QA confirmed it causes no desktop tab-order problem (CHANGE-97827).
				tabIndex={0}
				aria-label={label}
			>
				{Children.map(children, (child, index) => {
					if (!isValidElement(child)) return null;

					return (
						<li
							id={`scroll-container-list-item-${index}`}
							role="listitem"
							// eslint-disable-next-line react/no-array-index-key
							key={`${label}-${index}-${totalItems}`}
							className={cn(
								'flex snap-start [scroll-snap-stop:always] flex-col self-stretch [&>*]:min-h-0 [&>*]:flex-auto [&>*]:self-stretch',
								itemClassName,
							)}
						>
							{child}
						</li>
					);
				})}
			</ul>
		</As>
	);
};
